import React from 'react';
import './CreatorFrame.css';

/**
 * CreatorFrame — the shared full-screen "creator" chrome.
 *
 * This is the frame the gift-card designer (CustomDesignerPanel) lives in,
 * extracted so emails / gift cards / future creators share one screen and
 * one top-bar navigation. The body (rail, panels, canvas) is supplied by the
 * caller as children and changes per creator; the frame stays constant.
 *
 * Props:
 *   name          (string)   - editable design/campaign name
 *   onNameChange  (fn)        - if provided, the name renders as an input
 *   namePlaceholder (string)  - placeholder for the name input
 *   onClose       (fn)        - the X button (top-left)
 *   onCancel      (fn)        - Cancel button (defaults to onClose if omitted)
 *   onSave        (fn)        - Save button
 *   saveLabel     (string)    - Save button label (default "Save")
 *   saving        (bool)      - shows "Saving…" + disables Save
 *   saveDisabled  (bool)
 *   canUndo / canRedo (bool)
 *   onUndo / onRedo (fn)      - if neither provided, the history group is hidden
 *   error         (string)    - inline error shown left of the actions
 *   headerExtra   (node)      - extra controls left of Cancel/Save
 *   children      (node)      - the creator body
 */

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M3 3l8 8M11 3l-8 8" />
  </svg>
);
const UndoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 4L2.5 7.5 6 11" />
    <path d="M2.5 7.5H10a3.5 3.5 0 0 1 0 7H8.5" />
  </svg>
);
const RedoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 4l3.5 3.5L10 11" />
    <path d="M13.5 7.5H6a3.5 3.5 0 0 0 0 7h1.5" />
  </svg>
);

export default function CreatorFrame({
  name,
  onNameChange,
  namePlaceholder = 'Untitled design',
  onClose,
  onCancel,
  onSave,
  saveLabel = 'Save',
  saveVariant = 'primary',
  saving = false,
  saveDisabled = false,
  primaryAction = null, // { label, onClick, disabled } — charcoal primary, rightmost
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  error,
  headerExtra,
  children,
}) {
  const showHistory = !!(onUndo || onRedo);
  // Cancel only renders if explicitly provided — the X already covers it.
  const cancel = onCancel;

  return (
    <div className="cf-root" role="dialog" aria-modal="true">
      <header className="cf-topbar">
        <button type="button" className="cf-iconbtn" onClick={onClose} aria-label="Close" title="Close">
          <CloseIcon />
        </button>

        {showHistory && (
          <div className="cf-history">
            <button type="button" className="cf-iconbtn" onClick={onUndo} disabled={!canUndo} aria-label="Undo" title="Undo (⌘Z)">
              <UndoIcon />
            </button>
            <button type="button" className="cf-iconbtn" onClick={onRedo} disabled={!canRedo} aria-label="Redo" title="Redo (⇧⌘Z)">
              <RedoIcon />
            </button>
          </div>
        )}

        <div className="cf-name">
          {onNameChange ? (
            <input
              type="text"
              className="cf-name-input"
              placeholder={namePlaceholder}
              value={name || ''}
              onChange={(e) => onNameChange(e.target.value)}
              aria-label="Name"
            />
          ) : (
            <span className="cf-name-input" style={{ pointerEvents: 'none' }}>{name || namePlaceholder}</span>
          )}
        </div>

        <div className="cf-actions">
          {error && <span className="cf-error">{error}</span>}
          {headerExtra}
          {cancel && (
            <button type="button" className="cf-btn-ghost" onClick={cancel}>Cancel</button>
          )}
          {onSave && (
            <button
              type="button"
              className={saveVariant === 'secondary' ? 'cf-btn-secondary' : 'cf-btn-primary'}
              onClick={onSave}
              disabled={saveDisabled || saving}
            >
              {saving ? 'Saving…' : saveLabel}
            </button>
          )}
          {primaryAction && (
            <button
              type="button"
              className="cf-btn-primary"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </button>
          )}
        </div>
      </header>

      <div className="cf-body">
        {children}
      </div>
    </div>
  );
}
